import { Fragment, type HTMLAttributes, type ReactNode, type Ref } from 'react'
import { cx } from '@/shared/lib'
import { keySeparator, resolveKey, type KbdKey } from './keys'
import { resolvePlatform, type KbdPlatform } from './platform'
import s from './Kbd.module.css'

/** Собственная шкала: xs 20 (легенды подсказок), sm 24 (кнопка и поле), md 28 (крупное поле). */
export type KbdSize = 'xs' | 'sm' | 'md'

/**
 * `tint` — приглушённая плашка на светлой поверхности;
 * `outlined` — белый чип с волосяной рамкой там, где тинт садится на затонированный фон.
 */
export type KbdVariant = 'tint' | 'outlined'

export type KbdProps = {
  /** Клавиши сочетания по порядку: токены разворачиваются по платформе, прочие строки — как есть. */
  keys?: readonly KbdKey[]
  size?: KbdSize
  variant?: KbdVariant
  /** Раскладка подписей. `auto` спрашивает среду; `apple`/`other` нужны тестам, стенду и справке. */
  platform?: KbdPlatform
  /**
   * Полная фраза для ассистивных технологий («Control плюс K»).
   * Задана — видимое содержимое скрывается от скринридера, вместо него произносится фраза.
   * Нужна там, где чип состоит из глифов: «⌘» и «↑» словами не читаются.
   */
  label?: string
  /** Сочетание уже названо в aria-label родителя — чип уходит из дерева доступности. */
  decorative?: boolean
  /** Слот вместо `keys`: «↑↓» одной плашкой, «↵ Enter» глифом со словом. Перекрывает `keys`. */
  children?: ReactNode
  ref?: Ref<HTMLElement>
} & Omit<HTMLAttributes<HTMLElement>, 'children'>

// Карта классов при noUncheckedIndexedAccess приходит как string | undefined
const SIZE_CLASS: Record<KbdSize, string | undefined> = {
  xs: s.xs,
  sm: s.sm,
  md: s.md,
}

const VARIANT_CLASS: Record<KbdVariant, string | undefined> = {
  tint: s.tint,
  outlined: s.outlined,
}

/**
 * Колпачок клавиши: подсказка про ускоритель рядом с контролом.
 * Обработчик не вешает и о действии не знает — горячую клавишу регистрирует фича.
 * Контракт: docs/ui-kit/contracts/kbd.md
 */
export function Kbd({
  keys,
  size = 'sm',
  variant = 'tint',
  platform = 'auto',
  label,
  decorative = false,
  className,
  children,
  ref,
  ...rest
}: KbdProps) {
  const resolved = resolvePlatform(platform)
  const apple = resolved === 'apple'
  const separator = keySeparator(apple)

  const hasChildren = children !== undefined && children !== null
  const hasKeys = keys !== undefined && keys.length > 0

  // Пустая рамка в углу поля — визуальный мусор; прочерк вместо подсказки ставит потребитель
  if (!hasChildren && !hasKeys) return null

  const announced = Boolean(label) && !decorative

  return (
    <kbd
      {...rest}
      ref={ref}
      className={cx(s.root, SIZE_CLASS[size], VARIANT_CLASS[variant], className)}
      data-size={size}
      data-variant={variant}
      data-platform={resolved}
      aria-hidden={decorative || undefined}
    >
      <span className={s.content} aria-hidden={announced || undefined}>
        {hasChildren
          ? children
          : keys?.map((key, index) => (
              // Вложенные kbd — рекомендация спецификации: внешний «ввод», внутренние «клавиши»
              <Fragment key={`${key}-${index}`}>
                {index > 0 && separator !== '' ? separator : null}
                <kbd className={s.key}>{resolveKey(key, apple)}</kbd>
              </Fragment>
            ))}
      </span>
      {announced ? <span className={s.label}>{label}</span> : null}
    </kbd>
  )
}
